import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../store';
import {
  fetchLeagueAdministrators,
  assignLeagueAdministrator,
  revokeLeagueAdministrator,
  clearAdminError,
  clearAdminSuccess,
} from '../../store/slices/leagueSlice';

interface LeagueAdminManagerProps {
  leagueId: string;
}

/**
 * Super-Admin-only section on the League Detail page for managing which users
 * are designated as administrators of a specific league.
 *
 * Features:
 * - Lists current administrators with a Revoke button per row
 * - Assign-by-email input with friendly error messages for known error codes
 *   (already-assigned, user-not-found, not-assigned)
 * - Shows loading, error, and success feedback
 */
const LeagueAdminManager: React.FC<LeagueAdminManagerProps> = ({ leagueId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { administrators, adminLoading, adminError, adminSuccess } = useSelector(
    (state: RootState) => state.league,
  );

  const [emailInput, setEmailInput] = useState('');

  useEffect(() => {
    dispatch(fetchLeagueAdministrators(leagueId));
  }, [dispatch, leagueId]);

  // Clear success flag automatically after a short delay
  useEffect(() => {
    if (adminSuccess) {
      const timer = setTimeout(() => {
        dispatch(clearAdminSuccess());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [adminSuccess, dispatch]);

  const handleAssign = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = emailInput.trim();
    if (!trimmed) return;
    dispatch(clearAdminError());
    dispatch(assignLeagueAdministrator({ leagueId, email: trimmed })).then(() => {
      setEmailInput('');
    });
  };

  const handleRevoke = (userId: string) => {
    dispatch(clearAdminError());
    dispatch(revokeLeagueAdministrator({ leagueId, userId }));
  };

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">League Administrators</h3>
        <p className="mt-1 text-sm text-gray-500">
          Manage which users can administer this league. Changes take effect immediately.
        </p>

        {/* Error banner */}
        {adminError && (
          <div className="mt-4 rounded-md bg-danger-50 p-4" role="alert">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-danger-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-danger-800">{adminError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success banner */}
        {adminSuccess && (
          <div className="mt-4 rounded-md bg-green-50 p-4" role="status">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Administrator list updated successfully.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Current administrators list */}
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700">Current administrators</h4>
          {adminLoading && administrators.length === 0 ? (
            <div
              className="flex justify-center py-6"
              role="status"
              aria-label="Loading administrators"
            >
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-500"></div>
            </div>
          ) : administrators.length === 0 ? (
            <p className="mt-2 text-sm text-gray-500">No administrators assigned yet.</p>
          ) : (
            <ul className="mt-2 divide-y divide-gray-200 border border-gray-200 rounded-md">
              {administrators.map(admin => (
                <li key={admin.id} className="flex items-center justify-between py-3 px-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {admin.firstName} {admin.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{admin.email}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRevoke(admin.id)}
                    disabled={adminLoading}
                    className="ml-4 rounded-md bg-danger-600 px-3 py-1 text-sm font-medium text-white hover:bg-danger-700 disabled:opacity-50"
                  >
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Assign by email form */}
        <form onSubmit={handleAssign} className="mt-6">
          <h4 className="text-sm font-medium text-gray-700">Assign administrator by email</h4>
          <div className="mt-2 flex gap-3">
            <input
              id="admin-email-input"
              type="email"
              aria-label="Administrator email address"
              placeholder="user@example.com"
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              disabled={adminLoading}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={adminLoading || !emailInput.trim()}
              className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
            >
              {adminLoading ? 'Saving...' : 'Assign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeagueAdminManager;
